import React from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
} from '@the-platform-group/component-library/Modal'
import { Button } from '@the-platform-group/component-library/Button'
import { Grid } from '@the-platform-group/component-library/Layout'
import RequestForm from './RequestForm'
import AttachFilesButton from './AttachFilesButton'
import useEditRequestForm from '../hooks/useEditRequestForm'
import isEmpty from 'lodash/isEmpty'

/**
 * Modal component for editing client requests
 * @param {object} props - component props
 * @param {boolean} props.open - whether the modal is open
 * @param {function} props.onClose - function to close the modal
 * @param {object} props.request - the request data to edit
 * @returns {React.ReactElement} The rendered EditRequestModal component
 */
export default function EditRequestModal({ open, onClose, request }) {
  const {
    control,
    formState,
    setValue,
    handleSubmit,
    handleClose,
    isSubmitting,
    handleFileSelect,
    handleFileRemove,
    uploadedFiles,
    pendingFiles,
  } = useEditRequestForm(onClose, request)

  const { isValid } = formState

  // Don't render if no request is provided
  if (!request) {
    return null
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle title="Update request for client" />
      <ModalContent>
        <RequestForm
          control={control}
          formState={formState}
          setValue={setValue}
          uploadedFiles={uploadedFiles}
          pendingFiles={pendingFiles}
          onFileRemove={handleFileRemove}
          request={request}
          isEdit={true}
        />
      </ModalContent>
      <ModalActions>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mx: 2 }}>
          <Grid item>
            <AttachFilesButton
              onFileSelect={handleFileSelect}
              disabled={pendingFiles?.length > 0}
            />
          </Grid>
          <Grid item>
            <Grid container spacing={1}>
              <Grid item>
                <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={handleSubmit}
                  isLoading={isSubmitting || !isEmpty(pendingFiles)}
                  disabled={!isValid || !isEmpty(pendingFiles)}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ModalActions>
    </Modal>
  )
}

EditRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  request: PropTypes.object,
}
